import type { StreamProvider } from '@vgtv/api-client';
import { useEffect, useMemo } from 'preact/hooks';

import { resolveDnaStyleOption } from '../../../config/vendorToDnaBrand';

import { useColorScheme } from './useColorScheme';
import { useTheme } from './useTheme';

const THEME_ATTRIBUTE = 'data-theme';
const COLOR_SCHEME_ATTRIBUTE = 'data-color-scheme';

interface UseSkinContainerDecorationsParams {
  container: HTMLElement;
  vendor: StreamProvider;
  dnaStyle?: string;
  isAudio: boolean;
}

export const useSkinContainerDecorations = ({
  container,
  vendor,
  dnaStyle,
  isAudio,
}: UseSkinContainerDecorationsParams) => {
  const mode = useColorScheme();
  const theme = useTheme(container);

  const styleKey = resolveDnaStyleOption(vendor, dnaStyle);
  const dnaClass = `vgp-player-dna-${styleKey}`;

  const syncDecorationAttributes = (target: HTMLElement) => {
    if (theme) {
      target.setAttribute(THEME_ATTRIBUTE, theme);
    } else {
      target.removeAttribute(THEME_ATTRIBUTE);
    }

    if (mode) {
      target.setAttribute(COLOR_SCHEME_ATTRIBUTE, mode);
    } else {
      target.removeAttribute(COLOR_SCHEME_ATTRIBUTE);
    }
  };

  const audioContainer = useMemo(() => {
    if (!isAudio) {
      return null;
    }

    const existing = container.parentElement?.querySelector<HTMLElement>(
      '.vgp-audio-skin-container'
    );

    if (existing) {
      return existing;
    }

    const el = document.createElement('div');
    el.className = 'vgp-audio-skin-container';
    container.parentElement?.insertBefore(el, container.nextSibling);

    return el;
  }, [isAudio, container]);

  useEffect(() => {
    container.classList.add(dnaClass, 'vgp-player');
    syncDecorationAttributes(container);

    if (isAudio) {
      container.style.display = 'none';
      audioContainer?.classList.add(dnaClass, 'vgp-player');

      if (audioContainer) {
        syncDecorationAttributes(audioContainer);
      }
    }

    return () => {
      container.classList.remove(dnaClass, 'vgp-player');
      container.style.display = '';
      container.removeAttribute(THEME_ATTRIBUTE);
      container.removeAttribute(COLOR_SCHEME_ATTRIBUTE);
      audioContainer?.remove();
    };
  }, [container, dnaClass, isAudio, audioContainer, theme, mode]);

  return {
    mode,
    theme,
    dnaClass,
    audioContainer,
  };
};
